import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import { useNotify } from "react-admin";
import { organizationsApi } from "../providers/env";
import { CreateOrganizationRequest } from "../pages/RolesAndPermissions/types";

interface CreateOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateOrganizationModal: React.FC<
  CreateOrganizationModalProps
> = ({ open, onClose, onSuccess }) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreateOrganizationRequest>({
    name: "",
    description: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      notify("Organization name is required", { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      await organizationsApi.createOrganization({
        organizationCreate: form,
      });
      notify("Organization created successfully", { type: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      notify(
        error?.response?.data?.detail || "Error creating organization",
        { type: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({ name: "", description: "" });
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessIcon />
        Create New Organization
        <IconButton
          onClick={onClose}
          sx={{ ml: "auto" }}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a new organization. You can then add sub admins and users to
          this organization.
        </Typography>

        <TextField
          margin="normal"
          label="Organization Name"
          name="name"
          placeholder="e.g., Acme Corporation"
          fullWidth
          required
          value={form.name}
          onChange={handleChange}
          autoFocus
        />

        <TextField
          margin="normal"
          label="Description (Optional)"
          name="description"
          placeholder="Enter organization description"
          fullWidth
          multiline
          rows={3}
          value={form.description}
          onChange={handleChange}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          color="primary"
        >
          Create Organization
        </Button>
      </DialogActions>
    </Dialog>
  );
};